import { Action, createReducer, on } from '@ngrx/store';
import { EXECUTE_COMMAND_PIN_PERMISSION_EXPIRED_ERROR_CODE } from 'src/app/conf/permissions';

import { ItemsPagination } from 'src/app/model/api/data-rest';
import { CalendarException } from 'src/app/model/calendar-exception';
import { Permission, PermittedCommand, PermissionTemplate, PermittedThs, AccessPermission, AccessPermissionPoll } from 'src/app/model/permission';
import * as PermissionActions from './permissions.actions';

export interface State {
	paging?: ItemsPagination;
	permissions: Permission[];
	permissionsCalendar: Permission[];
	permission?: Permission;
	loading: boolean;
	permittedCommands: PermittedCommand[];
	permittedThs: PermittedThs[];
	accessPermission?: AccessPermission;
	permissionPoll?: AccessPermissionPoll;
	adding: boolean;
	added: boolean;
	editing: boolean;
	edited: boolean;
	deleting: boolean;
	deleted: boolean;
	pinResetting: boolean;
	pinResetted: boolean;
	pinSetting: boolean;
	pinSet: boolean;
	validPin: boolean;
	validToken: boolean;
	commandExecutionError: string;
	commandExecutionErrorId?: number;
	showPinPad: boolean;
	loadingTemplates: boolean;
	templates: PermissionTemplate[];
	currentTemplate?: PermissionTemplate;
	templateLogo?: string;
	templateUpdated: boolean;
	templateUpdating: boolean;
	exceptions: CalendarException[];
}

export const initialState: State = {
	permissions: [],
	permissionsCalendar: [],
	loading: false,
	permittedCommands: [],
	permittedThs: [],
	adding: false,
	added: false,
	editing: false,
	edited: false,
	deleting: false,
	deleted: false,
	pinResetting: false,
	pinResetted: false,
	pinSetting: false,
	pinSet: false,
	validPin: false,
	validToken: true,
	commandExecutionError: '',
	showPinPad: true,
	loadingTemplates: false,
	templates: [],
	templateUpdated: false,
	templateUpdating: false,
	exceptions: [],
};

const permissionReducer = createReducer(
	initialState,
	on(PermissionActions.getPermissionsSuccess, (state, payload) => ({
		...state, permissions: payload.permissions, paging: payload.paging, deleting: false, deleted: false
	})),
	on(PermissionActions.getPermittedCommandsSuccess, (state, payload) => ({
		...state, permittedCommands: payload.permittedCommands,
	})),
	on(PermissionActions.getPermittedThsSuccess, (state, payload) => ({
		...state, permittedThs: payload.permittedThs,
	})),
	on(PermissionActions.getPermissionsCalendarSuccess, (state, payload) => ({
		...state, permissionsCalendar: payload.permissionsCalendar,
	})),
	on(PermissionActions.getPermissionSuccess, (state, payload) => ({
		...state, permission: payload.permission,
	})),
	on(PermissionActions.resetPermissionForm, (state) => ({
		...state, permission: undefined, adding: false, added: false, editing: false, edited: false, deleting: false, deleted: false
	})),
	on(PermissionActions.addPermission, (state) => ({ ...state, adding: true, added: false })),
	on(PermissionActions.addPermissionSuccess, (state, payload) => {
		if (state.permissions !== undefined && state.paging !== undefined) {
			let statePermissions = state.permissions;
			if (state.permissions.length === state.paging.PageSize) {
				statePermissions = state.permissions.slice(0, state.permissions.length - 1);
			}
			return {
				...state,
				permissions: [payload.permission, ...statePermissions],
				paging: { ...state.paging, TotalCount: state.paging.TotalCount + 1 },
				adding: false, added: true
			};
		} else {
			return { ...state, adding: false, added: false };
		}
	}),
	on(PermissionActions.addPermissionFailed, (state) => ({ ...state, adding: false, added: false })),
	on(PermissionActions.editPermission, (state) => ({ ...state, editing: true, edited: false })),
	on(PermissionActions.editPermissionSuccess, (state, payload) => {
		const i = state.permissions.findIndex(p => p.Id === payload.permission.Id);
		const udtd = { ...state.permissions[i], ...payload.permission };
		const permissions = [...state.permissions.slice(0, i), udtd, ...state.permissions.slice(i + 1)];
		return { ...state, editing: false, edited: true, permissions };
	}),
	on(PermissionActions.editPermissionFailed, (state) => ({ ...state, editing: false, edited: false })),
	on(PermissionActions.deletePermission, (state) => ({ ...state, deleting: true, deleted: false })),
	on(PermissionActions.deletePermissionSuccess, (state) => ({ ...state, deleting: false, deleted: true })),
	on(PermissionActions.resetPin, (state) => ({ ...state, pinResetted: false })),
	on(PermissionActions.resetPinSuccess, (state) => ({ ...state, pinResetted: true })),
	on(PermissionActions.resetPinFailed, (state) => ({ ...state, pinResetted: true })),
	on(PermissionActions.getTokenPermissionCommandsSuccess, (state, payload) => ({
		...state, accessPermission: payload.accessPermission,
	})),
	on(PermissionActions.getTokenPermissionCommandsFailed, (state) => ({
		...state, validToken: false,
	})),
	on(PermissionActions.getPollPermissionAccessInfoThSuccess, (state, payload) => ({
		...state, permissionPoll: payload.permissionPoll
	})),
	on(PermissionActions.setPin, (state) => ({ ...state, pinSetting: true, pinSet: false })),
	on(PermissionActions.setPinSuccess, (state) => ({
		...state, pinSetting: false, pinSet: true,
	})),
	on(PermissionActions.setPinFailed, (state) => ({
		...state, pinSetting: false, pinSet: false
	})),
	on(PermissionActions.permissionStatusNotification, (state, payload) => {
		const permIndex = state.permissions.findIndex(p => p.Id === payload.permissionId);
		if (permIndex >= 0) {
			const updtPerm: Permission = { ...state.permissions[permIndex], Status: payload.status };
			return {
				...state,
				permissions: [...state.permissions.slice(0, permIndex), updtPerm, ...state.permissions.slice(permIndex + 1)],
			};
		}
		return { ...state };
	}),
	on(PermissionActions.executeTokenCommandSuccess, (state) => ({
		...state, validPin: true, validToken: true, showPinPad: true, commandExecutionErrorId: 0, commandExecutionError: '',
	})),
	on(PermissionActions.executeTokenCommandPinCheckFailed, (state, payload) => ({
		...state,
		validPin: false, validToken: true,
		commandExecutionError: payload.errorMessage,
		commandExecutionErrorId: payload.errorCode,
		showPinPad: payload.errorCode !== EXECUTE_COMMAND_PIN_PERMISSION_EXPIRED_ERROR_CODE
	})),
	on(PermissionActions.executeTokenCommandFailed, (state, payload) => ({
		...state, validToken: true, commandExecutionError: payload.errorMessage
	})),
	on(PermissionActions.getPermissionTemplatesSuccess, (state, payload) => ({
		...state, templates: payload.templates, currentTemplate: payload.templates[0],
	})),
	on(PermissionActions.resetPermissionTemplateForm, (state) => ({
		...state,
		templateUpdated: false, currentTemplate: undefined, templateLogo: undefined
	})),
	on(PermissionActions.updatePermissionTemplate, (state) => ({ ...state, templateUpdating: true })),
	on(PermissionActions.updatePermissionTemplateSuccess, (state) => ({ ...state, templateUpdated: true, templateUpdating: false })),
	on(PermissionActions.savePermissionTemplateSuccess, (state) => ({ ...state, templateUpdated: true })),
	on(PermissionActions.getPermissionTemplateLogoSuccess, (state, payload) => ({ ...state, templateLogo: payload.fileContent })),
	on(PermissionActions.resetPinTokenSuccess, (state) => ({
		...state, validPin: false, validToken: true, showPinPad: false,
		commandExecutionError: '',
	})),
	on(PermissionActions.resetPinTokenFailed, (state, payload) => ({
		...state, validToken: false, commandExecutionError: payload.errorMessage
	})),
	on(PermissionActions.deletePermissionExpired, (state) => ({ ...state, deleting: true, deleted: false })),
	on(PermissionActions.deletePermissionExpiredSuccess, (state) => ({ ...state, deleting: false, deleted: true })),
);

export const reducer = (state: State | undefined, action: Action): State => permissionReducer(state, action);
